import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Modal from '../modal';

// XPEN-3746: an administrator builds a GL segment's rules. Each rule is a set of
// conditions and one action; the first rule whose conditions all hold applies, and
// the segment's default value applies when none does. The API checks every value
// against the firm's GL chart when the client is saved.

export const CONDITION_FIELDS = ['account', 'document_type', 'department'];
export const DOCUMENT_TYPES = ['invoice', 'expense', 'card'];
const OPERATORS = ['in', 'not_in'];
const ACTION_TYPES = ['set', 'from_department', 'user_choose'];

const emptyRule = () => ({
    name: '',
    conditions: [{ field: 'account', operator: 'in', values: [] }],
    action: { type: 'set', value: '' },
});

const t = (data, key, fallback) => data?.t?.read(`segment_rules.${key}`) || fallback;

const ChipInput = ({ values, onChange, label, placeholder }) => {
    const [draft, setDraft] = useState('');
    const add = () => {
        const value = draft.trim();
        if (value && !values.includes(value)) onChange([...values, value]);
        setDraft('');
    };
    return (
        <div className="d-flex flex-wrap align-items-center form-control h-auto" style={{ gap: '6px', minWidth: '220px', flex: 1 }}>
            {values.map((value) => (
                <span key={value} className="badge badge-light border d-inline-flex align-items-center" style={{ gap: '4px', fontSize: '13px' }}>
                    {value}
                    <button type="button" className="btn btn-link p-0" aria-label={`Remove ${value}`}
                        onClick={() => onChange(values.filter((v) => v !== value))}>×</button>
                </span>
            ))}
            <input
                aria-label={label}
                className="border-0 flex-grow-1"
                style={{ outline: 'none', minWidth: '90px' }}
                value={draft}
                placeholder={placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        add();
                    }
                }}
                onBlur={add}
            />
        </div>
    );
};

export const describeRule = (rule, data) => {
    const join = (values) => (values || []).join(', ');
    const when = (rule.conditions || []).map((c) => {
        const field = t(data, `field_${c.field}`, c.field);
        const operator = t(data, `operator_${c.operator || 'in'}`, c.operator);
        const values = c.field === 'document_type'
            ? (c.values || []).map((v) => t(data, `document_${v}`, v)).join(', ')
            : join(c.values);
        return `${field} ${operator} ${values}`;
    });
    const action = rule.action || {};
    let then;
    if (action.type === 'user_choose') then = t(data, 'action_user_choose_summary', 'the user must choose');
    else if (action.type === 'from_department') {
        then = `${t(data, 'action_from_department_summary', "the submitter's department value")}`
            + ((action.allowed || []).length ? ` (${join(action.allowed)})` : '')
            + `, ${t(data, 'otherwise', 'otherwise')} ${action.fallback || '…'}`;
    } else then = action.value || '…';
    return { when: when.length ? when.join(` ${t(data, 'and', 'and')} `) : t(data, 'every_line', 'every line'), then };
};

const ruleIsComplete = (rule) => {
    const conditionsOk = (rule.conditions || []).every((c) => (c.values || []).length > 0);
    const action = rule.action || {};
    const actionOk = action.type === 'user_choose'
        || (action.type === 'set' && action.value)
        || (action.type === 'from_department' && action.fallback);
    return Boolean(rule.name.trim()) && conditionsOk && actionOk;
};

const RuleForm = ({ initial, segmentLabel, data, onDone, onCancel }) => {
    const [rule, setRule] = useState(initial);
    const setCondition = (index, patch) => setRule({
        ...rule,
        conditions: rule.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
    const action = rule.action;
    const preview = describeRule(rule, data);

    return (
        <div data-testid="segment-rule-form">
            <div className="form-group">
                <label htmlFor="segment-rule-name" className="font-weight-bold">{t(data, 'rule_name', 'Rule name')}</label>
                <input id="segment-rule-name" className="form-control" value={rule.name}
                    onChange={(e) => setRule({ ...rule, name: e.target.value })} />
            </div>

            <div className="mb-2 font-weight-bold">{t(data, 'when', 'When')} — {t(data, 'all_conditions', 'all of these conditions are true')}</div>
            {rule.conditions.map((condition, index) => (
                <div key={index} className="d-flex flex-wrap align-items-center border rounded p-2 mb-2" style={{ gap: '8px' }}
                    data-testid={`segment-rule-condition-${index}`}>
                    <select className="form-control w-auto" aria-label={t(data, 'condition_field', 'Field')} value={condition.field}
                        onChange={(e) => setCondition(index, { field: e.target.value, values: [] })}>
                        {CONDITION_FIELDS.map((f) => <option key={f} value={f}>{t(data, `field_${f}`, f)}</option>)}
                    </select>
                    <select className="form-control w-auto" aria-label={t(data, 'condition_operator', 'Operator')} value={condition.operator || 'in'}
                        onChange={(e) => setCondition(index, { operator: e.target.value })}>
                        {OPERATORS.map((o) => <option key={o} value={o}>{t(data, `operator_${o}`, o)}</option>)}
                    </select>
                    {condition.field === 'document_type' ? (
                        <div className="d-flex flex-wrap" style={{ gap: '14px' }}>
                            {DOCUMENT_TYPES.map((type) => (
                                <label key={type} className="mb-0 d-flex align-items-center" style={{ gap: '6px' }}>
                                    <input type="checkbox" checked={(condition.values || []).includes(type)}
                                        onChange={(e) => setCondition(index, {
                                            values: e.target.checked
                                                ? [...(condition.values || []), type]
                                                : (condition.values || []).filter((v) => v !== type),
                                        })} />
                                    {t(data, `document_${type}`, type)}
                                </label>
                            ))}
                        </div>
                    ) : (
                        <ChipInput values={condition.values || []} label={t(data, 'condition_values', 'Values')}
                            placeholder={t(data, 'type_and_enter', 'Type a code, then Enter')}
                            onChange={(values) => setCondition(index, { values })} />
                    )}
                    <button type="button" className="btn btn-outline-secondary btn-sm" aria-label={t(data, 'remove_condition', 'Remove condition')}
                        onClick={() => setRule({ ...rule, conditions: rule.conditions.filter((_, i) => i !== index) })}>×</button>
                </div>
            ))}
            <button type="button" className="btn btn-outline-primary btn-sm mb-3"
                onClick={() => setRule({ ...rule, conditions: [...rule.conditions, { field: 'account', operator: 'in', values: [] }] })}>
                + {t(data, 'add_condition', 'Add a condition')}
            </button>

            <div className="mb-2 font-weight-bold">{t(data, 'then', 'Then')} — {t(data, 'set_segment', 'set')} {segmentLabel}</div>
            <div className="d-flex flex-wrap mb-2" style={{ gap: '12px' }} role="radiogroup" aria-label={t(data, 'action', 'Action')}>
                {ACTION_TYPES.map((type) => (
                    <label key={type} className={`border rounded p-2 mb-0 d-flex align-items-center ${action.type === type ? 'border-primary' : ''}`} style={{ gap: '8px' }}>
                        <input type="radio" name="segment-rule-action" checked={action.type === type}
                            onChange={() => setRule({ ...rule, action: type === 'set' ? { type, value: '' } : type === 'from_department' ? { type, allowed: [], fallback: '' } : { type } })} />
                        {t(data, `action_${type}`, type)}
                    </label>
                ))}
            </div>
            {action.type === 'set' && (
                <div className="form-group">
                    <label htmlFor="segment-rule-value">{t(data, 'value', 'Value')}</label>
                    <input id="segment-rule-value" className="form-control w-auto" value={action.value || ''}
                        onChange={(e) => setRule({ ...rule, action: { ...action, value: e.target.value.trim() } })} />
                </div>
            )}
            {action.type === 'from_department' && (
                <div className="border rounded p-2 mb-2">
                    <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '8px' }}>
                        <span style={{ minWidth: '220px' }}>{t(data, 'allowed_values', 'Only these department values (empty: any)')}</span>
                        <ChipInput values={action.allowed || []} label={t(data, 'allowed_values', 'Allowed values')}
                            placeholder={t(data, 'type_and_enter', 'Type a code, then Enter')}
                            onChange={(allowed) => setRule({ ...rule, action: { ...action, allowed } })} />
                    </div>
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                        <label htmlFor="segment-rule-fallback" className="mb-0" style={{ minWidth: '220px' }}>{t(data, 'fallback', 'Otherwise, or no department')}</label>
                        <input id="segment-rule-fallback" className="form-control w-auto" value={action.fallback || ''}
                            onChange={(e) => setRule({ ...rule, action: { ...action, fallback: e.target.value.trim() } })} />
                    </div>
                </div>
            )}

            <div className="rounded p-3 mb-3" style={{ background: '#F0F4F8' }} data-testid="segment-rule-preview">
                <div className="small text-muted">{t(data, 'preview', 'Preview')}</div>
                <div>{t(data, 'when', 'When')} {preview.when}, {segmentLabel} = {preview.then}.</div>
            </div>

            <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
                <button type="button" className="btn btn-outline-dark" onClick={onCancel}>{t(data, 'cancel', 'Cancel')}</button>
                <button type="button" className="btn btn-primary" disabled={!ruleIsComplete(rule)} onClick={() => onDone(rule)}>
                    {t(data, 'done', 'Done')}
                </button>
            </div>
        </div>
    );
};

const SegmentRulesModal = ({ isOpen, segmentKey, segmentLabel, rules, defaultValue, data, onSave, onClose }) => {
    const [draft, setDraft] = useState([]);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setDraft(Array.isArray(rules) ? rules.map((r) => ({ ...r })) : []);
            setEditing(null);
        }
    }, [isOpen, rules]);

    if (!isOpen || !segmentKey) return null;

    const finishEdit = (rule) => {
        setDraft(editing === -1 ? [...draft, rule] : draft.map((r, i) => (i === editing ? rule : r)));
        setEditing(null);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const next = Array.from(draft);
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setDraft(next);
    };

    return (
        <Modal
            title={`${t(data, 'title', 'Rules for')} ${segmentLabel}`}
            width="900px"
            disableBackdropClose
            cancelAction={onClose}
            cancelText={t(data, 'cancel', 'Cancel')}
            okAction={editing === null ? () => { onSave(draft); onClose(); } : null}
            okText={t(data, 'save', 'Save rules')}
            noProcessingLoading
        >
            <div data-testid="segment-rules-modal">
                {editing !== null ? (
                    <RuleForm initial={editing === -1 ? emptyRule() : JSON.parse(JSON.stringify(draft[editing]))}
                        segmentLabel={segmentLabel} data={data} onDone={finishEdit} onCancel={() => setEditing(null)} />
                ) : (
                    <>
                        <p className="text-muted">{t(data, 'intro', 'Checked from top to bottom when a line\'s account is chosen; the first rule that matches sets the value. Drag a rule to change its priority.')}</p>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="segment-rules">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {draft.map((rule, index) => {
                                            const summary = describeRule(rule, data);
                                            return (
                                                <Draggable key={`${index}-${rule.name}`} draggableId={`rule-${index}`} index={index}>
                                                    {(drag) => (
                                                        <div ref={drag.innerRef} {...drag.draggableProps}
                                                            className="border rounded p-3 mb-2 d-flex align-items-start bg-white"
                                                            style={{ gap: '12px', ...drag.draggableProps.style }}
                                                            data-testid={`segment-rule-${index}`}>
                                                            <span {...drag.dragHandleProps} aria-label={t(data, 'drag', 'Drag to reorder')}
                                                                className="badge badge-dark" style={{ cursor: 'grab' }}>{index + 1}</span>
                                                            <div className="flex-grow-1">
                                                                <div className="font-weight-bold">{rule.name}</div>
                                                                <div className="small">
                                                                    <strong>{t(data, 'when', 'When')}</strong> {summary.when} · <strong>{t(data, 'then', 'Then')}</strong> {summary.then}
                                                                </div>
                                                            </div>
                                                            <button type="button" className="btn btn-link btn-sm" onClick={() => setEditing(index)}>
                                                                {t(data, 'edit', 'Edit')}
                                                            </button>
                                                            <button type="button" className="btn btn-outline-secondary btn-sm"
                                                                aria-label={`${t(data, 'delete', 'Delete')} ${rule.name}`}
                                                                onClick={() => setDraft(draft.filter((_, i) => i !== index))}>×</button>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        <button type="button" className="btn btn-outline-primary btn-block mb-3" style={{ borderStyle: 'dashed' }}
                            onClick={() => setEditing(-1)}>+ {t(data, 'add_rule', 'Add a rule')}</button>
                        <div className="border rounded p-3 d-flex align-items-center bg-light" style={{ gap: '12px' }} data-testid="segment-rules-otherwise">
                            <span className="badge badge-secondary">{t(data, 'last', 'Last')}</span>
                            <span><strong>{t(data, 'otherwise_title', 'Otherwise')}</strong> — {defaultValue
                                ? `${t(data, 'otherwise_default', 'the default value')} ${defaultValue}`
                                : t(data, 'otherwise_empty', 'no value (set a default value on the segment)')}</span>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default SegmentRulesModal;
